import { getShareParams } from '@/helpers/shareLink';
import { queryStoreeel } from '@/helpers/storeeelShadowDom';
import { sortContentGroupsForTileReadingOrder } from '@/helpers/tileRowOrder';
import { isReelLike } from '@/helpers/isReelLike';
import {
  buildFeedFolderContentId,
  buildFeedFolderGroupId,
  expandsFeedFolderAsGroups,
  isFeedFolderProductExpired,
} from '@/helpers/feedFolderResolver';

export const SHARE_OPEN_TIMEOUT_MS = 5000;

// 連携配信の共有リンク（storeeel_feed_product_id 付き）が指す展開元 content を探す。
// 共有URLの contentId は展開元の feed_folder content の id なので、window.storeeelData
// （展開前の /streams データ）にそのまま存在する。
const findFeedFolderShareContent = (campaign, params) => {
  if (!campaign || !params?.feedProductId || !params?.contentId) return null;

  const groups = campaign.contentGroups || [];
  for (const group of groups) {
    const content = (group?.contents || []).find(
      (item) => String(item?.id) === String(params.contentId) && item?.kind === 'feed_folder'
    );
    if (content) return { group, content };
  }

  return null;
};

// 共有された商品が候補プールに残っているかを確認し、残っていれば展開後の復元先IDを組み立てる。
// 商品が無い場合（GMC同期で掲載終了・表示停止日切れ）は null を返す。
const resolveFeedFolderShareTarget = (campaign, params, feedContent) => {
  if (!feedContent) return null;

  const product = (feedContent.content.feedProductCandidates || []).find(
    (candidate) => String(candidate?.id) === String(params.feedProductId)
  );
  // 期限切れ判定は抽選側（getFeedFolderPool）と同じにする。ここだけ通すと
  // 「共有先で開こうとするが、その商品はプールから除外されていて存在しない」ズレになる
  if (!product || isFeedFolderProductExpired(product)) return null;

  return {
    ...feedContent,
    product,
    // ピン留めにより商品は必ずプール先頭に来る（feedFolderResolver の resolveFeedFolderProducts）。
    // よって展開後の仮想IDは連番0で確定する
    contentId: buildFeedFolderContentId(feedContent.content.id, product.id, 0),
    groupId: expandsFeedFolderAsGroups(campaign)
      ? buildFeedFolderGroupId(feedContent.content.id, product.id, 0)
      : feedContent.group?.id ?? null,
  };
};

const resolveGroupIdFromContent = (campaign, contentId) => {
  if (!campaign || !contentId) return null;
  const groups = campaign.contentGroups || [];
  for (const group of groups) {
    const contents = group?.contents || [];
    const found = contents.find(content => String(content?.id) === String(contentId));
    if (found) return group?.id ?? null;
  }
  return null;
};

const isContentIdInCampaign = (campaign, contentId) => {
  if (!campaign || !contentId) return false;
  return (campaign.contentGroups || []).some(group =>
    Array.isArray(group?.contents)
    && group.contents.some(content => String(content?.id) === String(contentId))
  );
};

const isContentInGroup = (campaign, groupId, contentId) => {
  if (!campaign || !groupId || !contentId) return false;
  const group = (campaign.contentGroups || []).find(g => String(g?.id) === String(groupId));
  if (!group || !Array.isArray(group.contents)) return false;
  return group.contents.some(content => String(content?.id) === String(contentId));
};

const resolveSharedContent = (campaign, groupId, contentId) => {
  if (!campaign) return null;
  const groups = campaign.contentGroups || [];
  const targetGroup = groupId
    ? groups.find(g => String(g?.id) === String(groupId))
    : groups[0];
  if (!targetGroup || !Array.isArray(targetGroup.contents)) return null;

  if (contentId) {
    return targetGroup.contents.find(content => String(content?.id) === String(contentId)) || null;
  }
  return targetGroup.contents[0] || null;
};

const preloadMediaAsset = (mediaAsset) => {
  if (!mediaAsset?.mediaUrl) return;
  const mimeType = mediaAsset?.attachmentData?.metadata?.mime_type || '';
  if (mimeType.includes('image')) {
    const img = new Image();
    img.decoding = 'async';
    img.src = mediaAsset.mediaUrl;
    return;
  }

  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.src = mediaAsset.mediaUrl;
  try { video.load(); } catch { void 0; }
};

const resolveCampaignFromShareParams = (params) => {
  const campaigns = Array.isArray(window.storeeelData) ? window.storeeelData : [];
  if (!params) return null;

  if (params.campaignId) {
    const exact = campaigns.find(c => String(c.id) === String(params.campaignId));
    if (exact) return exact;
  }

  if (params.groupId) {
    const byGroup = campaigns.find(c =>
      Array.isArray(c.contentGroups)
      && c.contentGroups.some(g => String(g.id) === String(params.groupId))
    );
    if (byGroup) return byGroup;
  }

  if (params.contentId) {
    const byContent = campaigns.find(c =>
      Array.isArray(c.contentGroups)
      && c.contentGroups.some(g => g.contents?.some(content => String(content?.id) === String(params.contentId)))
    );
    if (byContent) return byContent;
  }

  return null;
};

const resolveInitialPosition = (campaign, params) => {
  if (!campaign || !params) return { iconId: null, moviePosition: 0 };

  const groups = campaign.contentGroups || [];
  let groupId = params.groupId;
  if (!groupId && params.contentId) {
    groupId = resolveGroupIdFromContent(campaign, params.contentId);
  }

  if (!groupId && groups.length > 0) {
    groupId = groups[0]?.id ?? null;
  }

  let moviePosition = 0;
  if (params.contentId && groupId) {
    const group = groups.find(g => String(g.id) === String(groupId));
    const idx = group?.contents?.findIndex(content => String(content?.id) === String(params.contentId));
    if (Number.isFinite(idx) && idx >= 0) moviePosition = idx;
  }

  // PC のリール型・タイル型は全コンテンツをフラット化したビューアのため、通しインデックスで解決する
  if (isReelLike(campaign.frame) && campaign.isMobile === false) {
    const flatContents = groups.flatMap(group => group?.contents || []);
    let targetIndex = -1;
    if (params.contentId) {
      targetIndex = flatContents.findIndex(content => String(content?.id) === String(params.contentId));
    } else if (groupId) {
      targetIndex = flatContents.findIndex(content => String(content?.contentGroupId) === String(groupId));
    }
    if (Number.isFinite(targetIndex) && targetIndex >= 0) {
      moviePosition = targetIndex;
    }
  }

  return { iconId: groupId, moviePosition };
};

export const openSharedContent = () => {
  const params = getShareParams();
  if (!params) return false;

  const campaign = resolveCampaignFromShareParams(params);
  if (!campaign) return false;

  // タイル型は行(tileRow)をまたいだナビゲーション順を見た目のグリッドと一致させる
  if (campaign.frame === 'tile') {
    campaign.contentGroups = sortContentGroupsForTileReadingOrder(campaign.contentGroups);
  }

  if (params.contentId && !isContentIdInCampaign(campaign, params.contentId)) {
    return false;
  }

  if (params.groupId && params.contentId && !isContentInGroup(campaign, params.groupId, params.contentId)) {
    return false;
  }

  // 連携配信は表示スロットが抽選で決まるため、通常コンテンツとは別経路で位置を決める。
  // 対象商品はピン留めでプール先頭に固定されるので、仮想グループ/仮想contentのIDが確定できる。
  const feedContent = findFeedFolderShareContent(campaign, params);
  const feedTarget = resolveFeedFolderShareTarget(campaign, params, feedContent);

  // 共有された商品が候補プールから消えている場合（掲載終了・表示停止日切れ・フィルタ条件の変更）。
  // 共有URLの contentId は展開元contentを指すため上の isContentIdInCampaign は通ってしまうが、
  // 実際に開ける表示スロットは存在しない。通常コンテンツで共有対象のcontentが配信から
  // 消えているときと同じく、フレームを問わず window を書き換えずに諦める
  // （共有リンクとしては何も起きず、キャンペーン自体は通常どおり表示される）。
  if (feedContent && !feedTarget) return false;

  const { iconId, moviePosition } = feedTarget
    // ストーリー型はグループ内での位置が展開結果に依存するため、ここでは先頭を仮置きし、
    // 展開後の実位置は campaignIconsTapAction が targetContentId から引き直す
    ? { iconId: feedTarget.groupId, moviePosition: 0 }
    : resolveInitialPosition(campaign, params);

  const preloadingContent = feedTarget
    ? null
    : resolveSharedContent(campaign, iconId ?? params.groupId, params.contentId);

  if (feedTarget) {
    // フィード商品は擬似mediaAsset（静止画）として描画されるため、商品画像を直接先読みする
    preloadMediaAsset({
      mediaUrl: feedTarget.product?.imageLink || '',
      attachmentData: { metadata: { mime_type: 'image/jpeg' } },
    });
  } else if (preloadingContent?.mediaAsset) {
    preloadMediaAsset(preloadingContent.mediaAsset);
  }

  window.storeeelItems = campaign;
  window.iconId = iconId != null ? String(iconId) : String(campaign.contentGroups?.[0]?.id ?? '');
  window.moviePosition = Number.isFinite(moviePosition) ? moviePosition : 0;
  window.__storeeelShareOpen = {
    source: 'link',
    params,
    // 描画側（createSlide の isShareTarget / setupVideoPlayerModal の表示待ち）が照合するのは
    // 実際に描画されるcontentのid。フィードは仮想IDになるため params.contentId とは別物になる
    // （商品が消えている場合はここへ来る前に return 済みなので、常に描画されるidが入る）
    targetContentId: feedTarget?.contentId ?? preloadingContent?.id ?? params.contentId ?? null,
    targetGroupId: iconId ?? params.groupId ?? null,
    iconId: window.iconId,
    moviePosition: window.moviePosition,
  };

  const icon = queryStoreeel(`#storeeel-icon-${window.iconId}`);
  if (icon) {
    icon.click();
    return true;
  }

  return false;
};
